import React, {useState, useEffect, memo, useContext } from 'react';
import {dbPost} from '../../apis/commonApi';
import DatePicker from '../../components/dateComponents/DatePicker2';
import CustomChart from '../../components/chartComponents/customChart';
import {formatDateToISOString} from '../../services/UtilService';
import { ModalContext } from '../../components/contexts';

const DateFilter = memo(({
    onDateChange,
    selectedPeriod,
    onPeriodSelect,
    customStartDate,
    customEndDate,
    setCustomStartDate,
    setCustomEndDate
}) => {
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
    const { modalService } = useContext(ModalContext);
    const [displayStartDate, setDisplayStartDate] = useState('');
    const [displayEndDate, setDisplayEndDate] = useState('');

    const formatDateForDisplay = date => {
            if (!date) return '';
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
    
        const validateDates = (date, comparisonDate, type) => {
            if (type === 'endDate' && !comparisonDate) {
                modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
                return false;
            }
    
            const selectedDate = new Date(date);
            const compareDate = comparisonDate ? new Date(comparisonDate) : null;
    
            if (type === 'startDate' && compareDate && selectedDate > compareDate) {
                modalService.errorAlert('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
                return false;
            }
    
            if (type === 'endDate' && compareDate && selectedDate < compareDate) {
                modalService.errorAlert('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
                return false;
            }
    
            return true;
        };
    
        const handleStartDateChange = (date) => {
            const formattedDate = formatDateToISOString(date); 
            const displayDate = formatDateForDisplay(date);   
            if (!validateDates(formattedDate, customEndDate, 'startDate')) {
                return;
            }
            setCustomStartDate(formattedDate);
            onDateChange({ startDate: formattedDate, endDate: customEndDate });
            setDisplayStartDate(displayDate);
        };
    
        const handleEndDateChange = (date) => {
            const formattedDate = formatDateToISOString(date); 
            const displayDate = formatDateForDisplay(date);  
            if (!validateDates(formattedDate, customStartDate, 'endDate')) {
                return;
            }
            setCustomEndDate(formattedDate); 
            onDateChange({ startDate: customStartDate, endDate: formattedDate });
            setDisplayEndDate(displayDate); 
        };

    return (
        <td>
            <div className="input-group">
                {
                    [
                        '전체',
                        '3개월',
                        '6개월',
                        '1년',
                        '2년',
                        '3년',
                        '기간 조회'
                    ].map((label, idx) => (
                        <div className="form-chip sm" key={idx}>
                            <input
                                type="radio"
                                className="radio"
                                name="rdo_chip_period"
                                id={`rdo_chip_period_${idx}`}
                                checked={selectedPeriod === label}
                                onChange={() => onPeriodSelect(label)}/>
                            <label
                                className="form-chip-outline"
                                htmlFor={`rdo_chip_period_${idx}`}
                                style={{
                                    height: 'auto'
                                }}>
                                {label}
                            </label>
                        </div>
                    ))
                }
            </div>
            {
                selectedPeriod === '기간 조회' && (
                    <div className="input-group gap-md">
                        <DatePicker
                        selectedDate={displayStartDate}
                        onDateChange={handleStartDateChange} 
                        label="시작 날짜"
                    />
                    <DatePicker
                        selectedDate={displayEndDate} 
                        onDateChange={handleEndDateChange} 
                        label="종료 날짜"
                    />
                    </div>
                )
            }
        </td>
    );
});

const ChartSection = memo(({title, chartData, chartType}) => {
    const transformedData = {
        categories: chartType === 'bar'
            ? chartData.map(item => item.taskNm)
            : [],
        series: chartType === 'bar'
            ? [
                {
                    name: '평균 완료 기간',
                    data: chartData.map(item => item.avgCompletionPeriod)
                }
            ]
            : chartData.map(([name, value]) => ({name, data: value}))
    };

    const options = {
        chart: {
            width: '100%',
            height: '400px',
            usageStatistics: false
        },
        xAxis: {
            title: '업종',
            pointOnColumn: true,
            label: {
                formatter: (value) => value.length > 5
                    ? value.substring(0, 5) + '...'
                    : value
            }
        },
        yAxis: {
            title: '평균 완료 기간'
        },
        series: {
            vertical: true
        },
        tooltip: {
            formatter: (value) => `${value}`
        }
    };

    return (
        <div>
            <strong className="content-tit">{title}</strong>
            <div className="txt-box bg-white gap-md">
                <CustomChart type={chartType} data={transformedData} options={options}/>
            </div>
        </div>
    );
});

const DataTable = ({title, headers, rows}) => (
    <div>
        <h3 className="block-tit">{title}</h3>
        <table className="tbl col data scroll">
            <caption>조회 결과 테이블입니다.</caption>
            <thead>
                <tr>
                    {headers.map((header, index) => (<th key={index} scope="col">{header}</th>))}
                </tr>
            </thead>
            <tbody>
                {
                    rows.map((row, index) => (
                        <tr key={index}>
                            {row.map((cell, idx) => (<td key={idx}>{cell}</td>))}
                        </tr>
                    ))
                }
            </tbody>
        </table>
    </div>
);

const ExptResStats = () => {
    useEffect(() => {
            document.title = '통계 - 월별 전문가 결과 현황 ';
          }, []);
    const [selectedPeriod, setSelectedPeriod] = useState('전체');
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analysisCauseData, setAnalysisCauseData] = useState([]);
    const [avgCompletionPeriodData, setAvgCompletionPeriodData] = useState([]);
    const [isCustomPeriod, setIsCustomPeriod] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(0);

    const fetchData = async (startDate, endDate) => {
        setIsLoading(true);
        try {
            const requestData = {
                startDate: startDate
                    ? startDate
                    : null,
                endDate: endDate
                    ? endDate
                    : null
            };
            const response = await dbPost('/stats/exptResStats/search.do', requestData);
            const {analysisCause, avgComplPeriod} = response;

            const pieData = [
                [
                    '인적', analysisCause[0]
                        ?.prYnCnt || 0
                ],
                [
                    '물적/설비적', analysisCause[0]
                        ?.phyYnCnt || 0
                ],
                [
                    '환경적', analysisCause[0]
                        ?.envYnCnt || 0
                ]
            ];
            setAnalysisCauseData(pieData);
            setAvgCompletionPeriodData(avgComplPeriod || []);
            setLastUpdated(Date.now());
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData(null, null);
    }, []);

    const handlePeriodSelect = (label) => {
        setSelectedPeriod(label);
        setIsCustomPeriod(label === '기간 조회');

        if (label !== '기간 조회') {
            const today = new Date();
            let startDate = null;
            let endDate = new Date();

            if (label === '3개월') {
                startDate = new Date(today.setMonth(today.getMonth() - 3));
            } else if (label === '6개월') {
                startDate = new Date(today.setMonth(today.getMonth() - 6));
            } else if (label === '1년') {
                startDate = new Date(today.setFullYear(today.getFullYear() - 1));
            } else if (label === '2년') {
                startDate = new Date(today.setFullYear(today.getFullYear() - 2));
            } else if (label === '3년') {
                startDate = new Date(today.setFullYear(today.getFullYear() - 3));
            }

            const formattedStartDate = formatDateToISOString(startDate);
            const formattedEndDate = formatDateToISOString(endDate, true);

            fetchData(formattedStartDate, formattedEndDate);
        }
    };

    const handleDateChange = ({ startDate, endDate }) => {
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
    };


    const handleSearch = () => {
        const startDate = formatDateToISOString(customStartDate);
        const endDate = formatDateToISOString(customEndDate, true);
        fetchData(startDate, endDate);
    };
    return (
        <div className="contents">
            <div className="page-title-wrap" data-type="responsive">
                <h2 className="h-tit">전문가 분석 결과 현황</h2>
            </div>

            <div className="tbl-wrap">
                <table className="tbl data row">
                    <caption>조회 조건 선택 테이블입니다.</caption>
                    <tbody>
                        <tr>
                            <th scope="row">조회 기간</th>
                            <DateFilter
                                selectedPeriod={selectedPeriod}
                                onPeriodSelect={handlePeriodSelect}
                                customStartDate={customStartDate}
                                customEndDate={customEndDate}
                                setCustomStartDate={setCustomStartDate}
                                setCustomEndDate={setCustomEndDate}
                                onDateChange={handleDateChange}
                            />
                        </tr>
                    </tbody>
                </table>
            </div>

            {
                selectedPeriod === '기간 조회' && (
                    <div className="page-btn-wrap gap-lg">
                        <button type="button" className="btn xlg primary" onClick={handleSearch}>
                            검색하기
                        </button>
                    </div>
                )
            }

            <div className="tbl-wrap both">
                {
                    isLoading
                        ? (<p>데이터를 불러오는 중입니다...</p>)
                        : (
                            <> < ChartSection title = "작업별 근본 원인" chartData = {
                                analysisCauseData
                            }
                            chartType = "pie" /> <ChartSection
                                title="업종별 평균 작업 완료 기간"
                                chartData={avgCompletionPeriodData}
                                chartType="bar"/>
                        </>
                        )
                }
            </div>

            <div className="tbl-wrap both">
                {
                    !isLoading && (
                        <> < DataTable title = "작업별 근본 원인 분석" headers = {
                            ['근본 원인', '작업수']
                        }
                        rows = {
                            analysisCauseData.map(([name, value]) => [name, value])
                        } /> <DataTable
                            title="업종별 평균 작업 완료 기간"
                            headers={['업종', '평균 완료 기간 (일)']}
                            rows={avgCompletionPeriodData.map(item => [item.taskNm, item.avgCompletionPeriod])}/>
                    </>
                    )
                }
            </div>
        </div>
    );
};

export default ExptResStats;
